import { Link } from "react-router-dom";
import { MoreHorizontal } from "lucide-react";
import { motion } from "framer-motion";

// Mock Data for the list
const podcasts = [
  {
    id: "suomi-news-jan-2026",
    title: "Suomi",
    subtitle: "Tammikuu 2026",
    badge: "Suomen Uutiset",
    fullTitle: "Suomen Päivän Uutiset — Tammikuu 2026",
    description: "Uutisartikkeleita ja Analyysejä Suomesta",
    date: "10.01.2026",
    duration: "15m",
    gradient: "from-[#4A7AB8] via-[#8B6BA3] to-[#E8A87C]",
    status: "new",
    progress: 0,
    link: "/episodes/1",
  },
  {
    id: "rahoitus-yel-2026",
    title: "Yrittäjyys",
    subtitle: "Rahoitus & YEL",
    badge: "Yrittäjä Opas",
    fullTitle: "Rahoituslaskelmat ja YEL-työtulo",
    description: "Finansman Hesaplamaları ve Girişimci Emeklilik Sigortası",
    date: "11.01.2026",
    duration: "15m",
    gradient: "from-[#2D8B6F] via-[#3AA07D] to-[#5BC4A0]",
    status: "new",
    progress: 0,
    link: "/episodes/2",
  },
  {
    id: "template-1",
    title: "Tulossa",
    subtitle: "Pian Saatavilla",
    badge: "Tuleva Jakso",
    fullTitle: "Gelecek Bölüm Başlığı",
    description: "Bu bölümün kısa açıklaması buraya gelecek.",
    date: "Yakında",
    duration: "--",
    gradient: "from-[#7B4B9E] via-[#9B5BA8] to-[#C77DBA]",
    status: "locked",
    progress: 0,
    link: "#",
  },
  {
    id: "template-2",
    title: "Kulttuuri",
    subtitle: "Suomen Taide",
    badge: "Kulttuuri",
    fullTitle: "Podcast Başlığı Şablonu",
    description: "Konuk ismi ve bölüm konusu hakkında detaylı açıklama.",
    date: "Yakında",
    duration: "--",
    gradient: "from-[#D85A4E] via-[#E87B5A] to-[#F4A574]",
    status: "locked",
    progress: 0,
    link: "#",
  },
];

interface PodcastData {
  id: string;
  title: string;
  subtitle: string;
  badge: string;
  fullTitle: string;
  description: string;
  date: string;
  duration: string;
  gradient: string;
  status: string;
  progress: number;
  link: string;
}

function PodcastCard({ data, index }: { data: PodcastData; index: number }) {
  const isLocked = data.status === "locked";

  const content = (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: index * 0.1 }}
      className="relative group"
    >
      <div
        className={`
          relative overflow-hidden rounded-2xl aspect-square
          bg-gradient-to-br ${data.gradient}
          hover:scale-[1.02] hover:shadow-2xl hover:shadow-black/30
          transition-all duration-300
          ${isLocked ? "opacity-60" : ""}
        `}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-white/10 pointer-events-none" />

        <div className="absolute top-3 md:top-4 left-0 right-0 md:left-auto md:right-4 flex justify-center md:justify-end">
          <span className="bg-white/20 backdrop-blur-md px-2 py-1 md:px-3 md:py-1.5 rounded-full text-[10px] md:text-xs font-medium text-white">
            {data.badge}
          </span>
        </div>

        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
          <h3 className="font-display text-4xl md:text-5xl text-white italic leading-tight">
            {data.title}
          </h3>
          <p className="text-white/90 text-sm md:text-base tracking-[0.2em] uppercase mt-2">
            {data.subtitle}
          </p>
        </div>

        {isLocked && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/30 backdrop-blur-[2px]">
            <span className="text-white/80 text-sm font-medium">Yakında</span>
          </div>
        )}
      </div>

      <div className="mt-3 px-1">
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <h4 className="text-white font-medium text-sm truncate">
              {data.fullTitle}
            </h4>
            <p className="text-txt-muted text-xs mt-0.5 truncate">
              {data.description}
            </p>
          </div>
          {!isLocked && (
            <button
              className="ml-2 p-2 rounded-full hover:bg-white/10 text-txt-secondary transition-colors"
              onClick={(e) => e.preventDefault()}
            >
              <MoreHorizontal className="w-5 h-5" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-3 mt-1 text-xs text-txt-muted">
          <span>{data.date}</span>
          <span>•</span>
          <span>{data.duration}</span>
        </div>
      </div>
    </motion.div>
  );

  if (isLocked) {
    return <div className="pointer-events-none">{content}</div>;
  }

  return <Link to={data.link}>{content}</Link>;
}

export default function App() {
  return (
    <main className="min-h-screen p-6 md:p-12 pb-24 font-body">
      <header className="mb-12 flex items-end justify-between">
        <div>
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-display text-4xl md:text-5xl font-bold text-white mb-2"
          >
            Keşfet
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-txt-secondary text-lg"
          >
            Sizin için son bölümler
          </motion.p>
        </div>

        <div className="flex gap-4"></div>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
        {podcasts.map((podcast, index) => (
          <PodcastCard key={podcast.id} data={podcast} index={index} />
        ))}
      </div>

      <footer className="mt-16 pt-8 border-t border-white/10 text-center text-xs text-txt-muted">
        <a
          href="https://project-info.mydesignbox.fi/gizlilik/"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-white transition-colors underline underline-offset-2"
        >
          Tietosuoja / Gizlilik
        </a>
      </footer>
    </main>
  );
}
